/**
 * Register Page for Y GO Shuttle Services France
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, UserPlus, AlertCircle } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, error: authError } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      await register(userData);
      navigate('/client');
    } catch (err) {
      setError(err.message || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4 py-12">
      <SEO title="Créer un compte client" description="Créez votre compte client Y GO pour réserver vos transferts VTC premium, suivre vos courses en temps réel et gérer toutes vos réservations en quelques clics." path="/register" />
      <h1 className="sr-only">Créer un compte client Y GO</h1>
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0B0B] via-[#1F1F1F] to-[#0B0B0B] opacity-50" />
      
      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <BrandMark size="2xl" className="justify-center" />
          <p className="text-[#E5E5E5] text-sm tracking-widest uppercase mt-2">
            Shuttle Services France
          </p>
        </div>

        <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] text-center">
              Créer un compte
            </CardTitle>
            <CardDescription className="text-[#E5E5E5]/60 text-center">
              Rejoignez Y GO pour réserver vos trajets
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(error || authError) && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error || authError}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-[#E5E5E5]">Prénom</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    placeholder="Jean"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D]"
                    data-testid="register-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-[#E5E5E5]">Nom</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    placeholder="Dupont"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D]"
                    data-testid="register-lastname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#E5E5E5]">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre@email.fr"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D]"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#E5E5E5]">Téléphone (optionnel)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+33 6 12 34 56 78"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D]"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#E5E5E5]">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D] pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#E5E5E5]/40 hover:text-[#C9A24D] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-[#E5E5E5]/40">Min. 8 caractères, 1 majuscule, 1 chiffre</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#E5E5E5]">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D]"
                  data-testid="register-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold transition-all duration-300"
                data-testid="register-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-[#0B0B0B]/30 border-t-[#0B0B0B] rounded-full animate-spin" />
                    Création...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Créer mon compte
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#E5E5E5]/10">
              <p className="text-center text-[#E5E5E5]/60 text-sm">
                Déjà un compte ?{' '}
                <Link 
                  to="/login" 
                  className="text-[#C9A24D] hover:text-[#E6C77A] transition-colors font-medium"
                >
                  Se connecter
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[#E5E5E5]/40 text-xs mt-8">
          © 2024 Y GO Shuttle Services France. Tous droits réservés.
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
